import { Injectable } from '@angular/core';
import { Observable, filter, switchMap, take, Subject, startWith, map, withLatestFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Cuenta, CuentaForm, Credential } from '@core/models';
import { environment } from '@environments/environment';
import {
  ResponseSuccess,
  ResponseObjectSuccess,
} from '@interfaces/response-success';
import { AuthenticationState } from '@store/reducers/authentication.reducer';
import { selectCredential } from '@store/selectors/authentication.selectors';
import { objectToFormData } from '@core/helpers/formdata';
import { transformAccountUpsert } from '@core/helpers';
import { selectAccountList, selectAuthenticationIsSuperUser, selectAuthenticationIsReseller } from '@store/selectors';
import { transformDropdown } from '@core/helpers';
import { MenuItem } from 'primeng/api';
import { StateService } from '@core/services';
import { StateEnum } from '@core/enums';

@Injectable({ providedIn: 'root' })
export class AccountService {
  credential$: Observable<Credential | null>;
  accountList$: Observable<Cuenta[] | null>;

  private createCompletedSource = new Subject<void>();
  createCompleted$ = this.createCompletedSource.asObservable();

  private updateCompletedSource = new Subject<void>();
  updateCompleted$ = this.updateCompletedSource.asObservable();

  private removeCompletedSource = new Subject<void>();
  removeCompleted$ = this.removeCompletedSource.asObservable();

  private closeDialogSource = new Subject<void>();
  closeDialog$ = this.closeDialogSource.asObservable();

  constructor(
    private http: HttpClient,
    private store: Store<AuthenticationState>,
    private stateService: StateService,
  ) {
    this.credential$ = this.store.select(selectCredential);
    this.accountList$ = this.store.select(selectAccountList);
  }

  createCompleted(): void {
    this.createCompletedSource.next();
    this.closeDialog();
  }

  updateCompleted(): void {
    this.updateCompletedSource.next();
    this.closeDialog();
  }

  removeCompleted(): void {
    this.removeCompletedSource.next();
    this.closeDialog();
  }

  closeDialog(): void {
    this.closeDialogSource.next();
  }

  find(id: string): Observable<ResponseSuccess<Cuenta>> {
    return this.credential$.pipe(
      filter((credential): credential is Credential => !!credential && !!credential.granted),
      switchMap((credential) => {
        const token = credential?.token ? credential.token : credential?.apiKey ? credential.apiKey : '';

        return this.http.get<ResponseSuccess<Cuenta>>(
          `${environment.microConfiguraciones}/api/v1/cuentas/${id}`,
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }

  findAll({
    resellerId,
    name,
  }: {
    resellerId?: string;
    name?: string;
  }): Observable<ResponseSuccess<Cuenta>> {
    return this.credential$.pipe(
      filter((credential): credential is Credential => !!credential && !!credential.granted),
      withLatestFrom(
        this.store.select(selectAuthenticationIsSuperUser),
        this.store.select(selectAuthenticationIsReseller)
      ),
      switchMap(([ credential, isSuperUser, isReseller ]) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        const params = {
          ...(!!name && { nombre: name }),
          ...(!!resellerId && { resellerId }),
          ...(!!isReseller && { resellerId: credential.idUsuario }),
          ...(!isSuperUser && !isReseller && { resellerId: credential.resellerId }),
        };

        return this.http.get<ResponseSuccess<Cuenta>>(
          `${environment.microConfiguraciones}/api/v1/cuentas`,
          {
            headers: { Authorization: token },
            params: { ...params, incluyeInactivos: true },
          }
        );
      }),
      take(1),
    );
  }

  create(
    accountForm: CuentaForm,
  ): Observable<ResponseObjectSuccess<Cuenta>> {
    return this.credential$.pipe(
      filter((credential): credential is Credential => !!credential && !!credential.granted),
      withLatestFrom(
        this.stateService.getStateByName(StateEnum.Active),
        this.store.select(selectAuthenticationIsSuperUser),
        this.store.select(selectAuthenticationIsReseller)
      ),
      switchMap(([ credential, state, isSuperUser, isReseller ]) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.post<ResponseObjectSuccess<Cuenta>>(
          `${environment.microConfiguraciones}/api/v1/cuentas`,
          objectToFormData(
            transformAccountUpsert({
              ...accountForm,
              statusId: state?.idEstado as string,
              resellerId: !!isSuperUser 
                ? accountForm.resellerId 
                : !!isReseller 
                  ? credential.idUsuario 
                  : credential.resellerId,
            })
          ),
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }

  update(
    id: string,
    accountForm: CuentaForm
  ): Observable<ResponseObjectSuccess<Cuenta>> {
    return this.credential$.pipe(
      filter((credential): credential is Credential => !!credential && !!credential.granted),
      withLatestFrom(
        this.stateService.getStateByName(StateEnum.Active),
        this.store.select(selectAuthenticationIsSuperUser),
        this.store.select(selectAuthenticationIsReseller)
      ),
      switchMap(([ credential, state, isSuperUser, isReseller ]) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.patch<ResponseObjectSuccess<Cuenta>>(
          `${environment.microConfiguraciones}/api/v1/cuentas/${id}`,
          objectToFormData(
            transformAccountUpsert({
              ...accountForm,
              statusId: state?.idEstado as string,
              resellerId: !!isSuperUser 
                ? accountForm.resellerId 
                : !!isReseller 
                  ? credential.idUsuario 
                  : credential.resellerId,
            })
          ),
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }

  remove(id: string): Observable<ResponseObjectSuccess<Cuenta>> {
    return this.credential$.pipe(
      filter((credential): credential is Credential => !!credential && !!credential.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.delete<ResponseObjectSuccess<Cuenta>>(
          `${environment.microConfiguraciones}/api/v1/cuentas/${id}`,
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }

  activate(id: string): Observable<ResponseObjectSuccess<Cuenta>> {
    return this.credential$.pipe(
      filter((credential): credential is Credential => !!credential && !!credential.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.patch<ResponseObjectSuccess<Cuenta>>(
          `${environment.microConfiguraciones}/api/v1/cuentas/${id}/activar`,
          {},
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }

  deactivate(
    id: string,
    razonInactivacion: string
  ): Observable<ResponseObjectSuccess<Cuenta>> {
    return this.credential$.pipe(
      filter((credential): credential is Credential => !!credential && !!credential.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.patch<ResponseObjectSuccess<Cuenta>>(
          `${environment.microConfiguraciones}/api/v1/cuentas/${id}/desactivar`,
          { razonInactivacion: razonInactivacion.trim() },
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }

  getDropdown(): Observable<MenuItem[]> {
    return this.accountList$.pipe(
      startWith([]),
      filter((accountList): accountList is Cuenta[] => !!accountList),
      map((accountList) => transformDropdown(accountList, 'idCuenta', 'nombre')),
    );
  }
}
